import { useEffect, useMemo, useRef, useState } from 'react';
import { CameraDot, Notice, Panel, Seal, TargetPicker } from '../components/Bits';
import { MusicVolumeControl, VolumeControl } from '../components/VolumeControl';
import { phaseName, roleName, WIN_NAMES } from '../lib/format';
import {
  getCameraBrowserHelp,
  getVideoDevices,
  openMediaMTXCamera,
  playerCameraPath,
  playerCameraUrl,
  type CameraDevice,
} from '../lib/mediamtxCamera';
import { ambience, soundBoard, type Cue } from '../lib/sound';
import { ambienceFor, CROSSFADE_MS, MEDIA } from '../lib/media';
import type { Connection } from '../lib/socket';
import type { PlayerState } from '../lib/types';

interface Props {
  connection: Connection<PlayerState>;
  onLeave: () => void;
  cameraToken: string;
}

const PHASE_CUES: Partial<Record<string, Cue>> = {
  NIGHT_START: 'NIGHT',
  MORNING_REVEAL: 'MORNING',
  DAY_VOTE: 'VOTING',
  VOTE_REVEAL: 'VOTE_REVEAL',
  GAME_END: 'VICTORY',
};

export function PlayerScreen({ connection, onLeave, cameraToken }: Props) {
  const view = connection.view;
  const [choice, setChoice] = useState<string | null>(null);
  const [poison, setPoison] = useState<string | null>(null);
  const [heal, setHeal] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [letter, setLetter] = useState('');

  const kind = view?.action?.kind ?? null;
  const actionId = view?.action?.current?.actionId ?? null;
  const locked = view?.action?.current?.status === 'CONFIRMED';

  // Neue Aktion, neue Auswahl.
  useEffect(() => {
    setChoice(null);
    setPoison(null);
    setHeal(false);
    setLetter('');
    setMessage(null);
  }, [kind, view?.round]);

  // Bestätigte Entscheidungen erscheinen wieder in der Auswahl.
  useEffect(() => {
    const payload = view?.action?.current?.payload;
    if (!payload) return;

    if (payload.targetId !== undefined) {
      setChoice(payload.targetId ?? null);
    }

    if (payload.heal !== undefined) {
      setHeal(!!payload.heal);
    }

    if (payload.poisonTargetId !== undefined) {
      setPoison(payload.poisonTargetId ?? null);
    }
  }, [actionId, view?.action?.current?.status]);

  useEffect(() => {
    if (!view) return;

    const cue = PHASE_CUES[view.phase];

    if (cue) {
      void soundBoard.play(cue);
    }
  }, [view?.phase]);

  // Musik läuft auch in der Spieleransicht – jeder regelt sie für sich.
  const mood = view
    ? ambienceFor(
        view.phase.includes('PHASE') || view.phase === 'NIGHT_START'
          ? 'NIGHT'
          : 'DAY',
        view.phase,
      )
    : 'START';

  useEffect(() => {
    ambience.crossfadeTo(MEDIA[mood].music, CROSSFADE_MS);
  }, [mood]);

  useEffect(() => () => ambience.stop(), []);

  const players = useMemo(() => view?.players ?? [], [view]);

  /** Wen der Priester zuletzt geschützt hat – diese Person fehlt heute in der Auswahl. */
  const lastProtected = view?.resources?.lastProtectedId ?? null;

  const confirmedSummary = useMemo(() => {
    const a = view?.action?.current;

    if (!a) {
      return '';
    }

    const who = (id: string | null | undefined) =>
      players.find((p) => p.id === id)?.name ?? '—';

    if (view?.action?.kind === 'WITCH_POTIONS') {
      const parts: string[] = [];

      if (a.payload.heal) {
        parts.push('Heiltrank eingesetzt');
      }

      if (a.payload.poisonTargetId) {
        parts.push(`${who(a.payload.poisonTargetId)} vergiftet`);
      }

      return parts.length > 0
        ? parts.join(', ') + '.'
        : 'Kein Trank eingesetzt.';
    }

    return a.payload.targetId
      ? `Du hast ${who(a.payload.targetId)} gewählt.`
      : '';
  }, [view?.action, players]);

  const nameOf = (id: string | null | undefined) =>
    players.find((p) => p.id === id)?.name ?? '—';

  if (connection.status === 'closed') {
    return (
      <main className="page">
        <Notice>Die Lobby wurde vom Spielleiter geschlossen.</Notice>
        <button
          type="button"
          style={{ marginTop: '1rem' }}
          onClick={onLeave}
        >
          Zurück
        </button>
      </main>
    );
  }

  if (connection.status === 'error') {
    return (
      <main className="page">
        <Notice>{connection.error}</Notice>

        <button
          type="button"
          style={{ marginTop: '1rem' }}
          onClick={onLeave}
        >
          Neu beitreten
        </button>
      </main>
    );
  }

  if (!view) {
    return (
      <main className="page">
        <p className="muted pulse">
          Die Tore von Noxbury öffnen sich …
        </p>
      </main>
    );
  }

  const me = view.me;
  const dead = me.status === 'DEAD';
  const waiting = me.status === 'WAITING';

  /**
   * Ein Klick genügt: Die Wahl wird gesendet und sofort festgeschrieben.
   * Bei den Vampiren gilt sie erst, wenn beide dasselbe Ziel gewählt haben –
   * bis dahin bleibt sie änderbar.
   */
  async function submit(payload: Record<string, unknown>) {
    const sent = await connection.send({
      type: 'SUBMIT_ACTION',
      kind,
      payload,
      confirm: true,
    });

    if (!sent.ok) {
      setMessage(sent.message ?? 'Das hat nicht geklappt.');
      return;
    }

    setMessage(null);
  }

  return (
    <main className="page stack">
      <header
        className="row"
        style={{ justifyContent: 'space-between' }}
      >
        <div>
          <p
            className="tiny muted"
            style={{ margin: 0 }}
          >
            Runde {Math.max(1, view.round)} · {phaseName(view.phase)}
          </p>

          <h1 style={{ fontSize: '1.6rem' }}>
            Noxbury
          </h1>
        </div>

        <div className="row">
          <CameraDot status={view.camera.status} />

          <span className="tag">
            {view.paused ? 'Pause' : `Nr. ${me.number}`}
          </span>
        </div>
      </header>

      {waiting ? (
        <Notice calm>
          <strong>⏳ Du bist in der Warteschlange</strong>
          <p style={{ marginBottom: 0 }}>
            Aktuell läuft noch eine Runde. Sobald diese beendet ist, geht es für dich los!
          </p>
        </Notice>
      ) : (
        <>
          {view.paused && (
            <Notice calm>
              Der Spielleiter hat die Partie angehalten.
            </Notice>
          )}

      <section className="dossier">
        <div className="dossier-head">
          {me.role && (
            <RolePicture role={me.role} />
          )}

          <div>
            <p
              className="tiny muted"
              style={{ margin: 0 }}
            >
              {me.name}, {dead ? 'gefallen' : 'lebend'}
              {me.isMayor ? ' · Bürgermeister' : ''}
            </p>

            <p className="role-name">
              {roleName(me.role)}
            </p>

            <p>
              {me.roleDescription}
            </p>
          </div>
        </div>

        {me.wasDrunk && (
          <p className="tiny muted">
            Der Rausch ist verflogen – das war deine wahre Rolle.
          </p>
        )}

        {me.foolConverted && (
          <p className="tiny muted">
            Du bist jetzt ein gewöhnlicher Dorfbewohner.
          </p>
        )}

        {me.winCondition && (
          <p className="oath">
            {me.winCondition}
          </p>
        )}
      </section>

      {view.ghost && (
        <Notice>
          <RolePicture
            role="GHOST"
            inline
          />

          Du bist zum Geist geworden. Deine ursprüngliche Rolle bleibt bestehen.
        </Notice>
      )}

      {view.lover && (
        <Notice>
          <RolePicture
            role="LOVERS"
            inline
          />

          Dein Herz gehört <strong>{view.lover.name}</strong>.{' '}
          {view.lover.alive
            ? 'Stirbt eine oder einer von euch, stirbt auch die andere Seite.'
            : 'Diese Liebe hat den Tod nicht überlebt.'}
        </Notice>
      )}

      {view.knownVampires.length > 0 && (
        <Notice>
          Im Bunde mit dir:{' '}
          {view.knownVampires.map((v) => v.name).join(', ')}
        </Notice>
      )}

      {view.morningDeaths && (
        <Panel title="Der Morgen">
          {view.morningDeaths.length === 0 ? (
            <p>
              Diese Nacht hat Noxbury niemanden verloren.
            </p>
          ) : (
            <p>
              Gefunden wurden:{' '}
              {view.morningDeaths.map(nameOf).join(', ')}.
            </p>
          )}
        </Panel>
      )}

      {view.action && (
        <Panel title="Du bist an der Reihe">
          {kind === 'WITCH_POTIONS' ? (
            <div className="stack">
              <div>
                <h3 className="ask">
                  {view.action.witch?.healAvailable
                    ? view.action.witch.vampireTargetId
                      ? `Möchtest du ${nameOf(
                          view.action.witch.vampireTargetId,
                        )} retten und deinen Heiltrank verwenden?`
                      : 'Heute Nacht gibt es niemanden zu retten.'
                    : 'Dein Heiltrank wurde bereits benutzt.'}
                </h3>

                {view.action.witch?.healAvailable &&
                  view.action.witch.vampireTargetId && (
                    <div className="choice-row">
                      <button
                        type="button"
                        className={heal ? 'selected' : ''}
                        disabled={locked}
                        onClick={() => setHeal(true)}
                      >
                        Ja
                      </button>

                      <button
                        type="button"
                        className={!heal ? 'selected' : ''}
                        disabled={locked}
                        onClick={() => setHeal(false)}
                      >
                        Nein
                      </button>
                    </div>
                  )}
              </div>

              <div>
                <h3 className="ask">
                  {view.action.witch?.poisonAvailable
                    ? 'Möchtest du jemanden vergiften?'
                    : 'Dein Gifttrank wurde bereits benutzt.'}
                </h3>

                {view.action.witch?.poisonAvailable && (
                  <TargetPicker
                    players={players}
                    allowed={view.action.targets}
                    value={poison}
                    onChange={(id) =>
                      setPoison(id === poison ? null : id)
                    }
                    disabled={locked}
                  />
                )}
              </div>

              <div className="choice-row">
                <ConfirmButton
                  locked={locked}
                  onClick={() =>
                    void submit({
                      heal,
                      poisonTargetId: poison,
                    })
                  }
                />
                <button
                  type="button"
                  disabled={locked}
                  onClick={() =>
                    void submit({
                      heal: false,
                      poisonTargetId: null,
                    })
                  }
                >
                  Nichts tun
                </button>
              </div>
            </div>
          ) : kind === 'GHOST_LETTER' ? (
            <div className="stack">
              <h3 className="ask">
                Welchen Buchstaben hinterlässt du den Lebenden?
              </h3>

              <div className="letter-row">
                <input
                  className="letter-input"
                  value={letter}
                  maxLength={1}
                  autoCapitalize="characters"
                  inputMode="text"
                  aria-label="Buchstabe"
                  disabled={locked}
                  onChange={(e) =>
                    setLetter(
                      e.target.value
                        .toUpperCase()
                        .replace(/[^A-ZÄÖÜ]/g, ''),
                    )
                  }
                />
              </div>

              {view.ghost &&
                view.ghost.letters.length > 0 && (
                  <p className="tiny muted">
                    Bisher steht dort:{' '}
                    {view.ghost.letters.join(' ')}
                  </p>
                )}

              <ConfirmButton
                locked={locked}
                disabled={!letter}
                onClick={() =>
                  void submit({ letter })
                }
              />
            </div>
          ) : (
            <div className="stack">
              <h3 className="ask">
                {view.action.prompt}
              </h3>

              <TargetPicker
                players={players}
                allowed={view.action.targets}
                value={choice}
                onChange={setChoice}
                disabled={locked}
              />

              {kind === 'VAMPIRE_ATTACK' && view.vampireChoices.length > 0 && (
                <Notice calm>
                  {view.vampireChoices.map((v) => (
                    <div key={v.vampireId}>
                      <strong>{v.vampireName}</strong> hat gewählt: <strong>{v.targetName}</strong>
                    </div>
                  ))}
                </Notice>
              )}

              {kind === 'PRIEST_PROTECT' &&
                lastProtected && (
                  <p className="tiny muted">
                    Du kannst nicht zweimal hintereinander denselben
                    Spieler schützen – {nameOf(lastProtected)} steht
                    heute Nacht nicht zur Wahl.
                  </p>
                )}

              <ConfirmButton
                locked={locked}
                disabled={!choice}
                onClick={() =>
                  void submit({
                    targetId: choice,
                  })
                }
              />
            </div>
          )}

          {locked && (
            <p className="chosen">
              {confirmedSummary}
            </p>
          )}

          {view.action.current?.status === 'PENDING' && (
            <p
              className="tiny muted"
              style={{ marginTop: '0.7rem' }}
            >
              Gesendet. Es gilt, sobald ihr Vampire euch einig seid –
              bis dahin kannst du wechseln.
            </p>
          )}

          {view.action.current?.status === 'REJECTED' && (
            <p className="tiny">
              Der Spielleiter hat das abgelehnt. Bitte neu wählen.
            </p>
          )}

          {locked && (
            <p className="tiny muted">
              Diese Wahl steht fest. Nur der Spielleiter kann sie noch
              korrigieren.
            </p>
          )}

          {message && !locked && (
            <p className="tiny muted">
              {message}
            </p>
          )}
        </Panel>
      )}

      {view.possessed.length > 0 && (
        <Panel title="Von dir besessen">
          <p>
            {view.possessed
              .map((id) => nameOf(id))
              .join(', ')}
          </p>
        </Panel>
      )}

      {view.ghost && (
        <Panel title="Deine Botschaft">
          <p className="ghost-line">
            {view.ghost.letters.join(' ') || '—'}
          </p>

          {view.ghost.pending && (
            <p className="tiny muted">
              Für diese Nacht liegt „{view.ghost.pending}“ bereit. Der
              Spielleiter gibt ihn am Morgen frei.
            </p>
          )}
        </Panel>
      )}

      {view.seerResults.length > 0 && (
        <Panel title="Deine Erkenntnis">
          {(() => {
            const letzte =
              view.seerResults[
                view.seerResults.length - 1
              ]!;

            return (
              <ul className="findings">
                <li className="tonight">
                  {nameOf(letzte.targetId)} – {letzte.text}
                </li>
              </ul>
            );
          })()}
        </Panel>
      )}

      {view.dayVote?.revealed &&
        view.dayVote.counts && (
          <Panel title="Die Stimmen">
            <ul
              style={{
                margin: 0,
                paddingLeft: '1.1rem',
              }}
            >
              {view.dayVote.counts.map((c) => (
                <li key={c.playerId}>
                  {nameOf(c.playerId)}: {c.votes}
                </li>
              ))}
            </ul>
          </Panel>
        )}

      <Panel title="Spielerliste">
        <ul className="roster">
          {players.map((p) => (
            <li
              key={p.id}
              className={
                p.status === 'DEAD'
                  ? 'gone'
                  : ''
              }
            >
              <span className="roster-name">
                {p.name}
              </span>

              {p.isMayor && (
                <span className="tag mayor tiny">
                  BM
                </span>
              )}
            </li>
          ))}
        </ul>
      </Panel>

      {view.result && (
        <Panel title="Ende der Partie">
          <p>
            Es gewinnt:{' '}
            <strong>
              {view.result.winners
                .map(
                  (w) =>
                    WIN_NAMES[w] ?? w,
                )
                .join(', ')}
            </strong>
          </p>

          <ul
            style={{
              margin: 0,
              paddingLeft: '1.1rem',
            }}
          >
            {view.result.roles.map((r) => (
              <li key={r.playerId}>
                {nameOf(r.playerId)}:{' '}
                {roleName(r.role)}
                {r.wasDrunk
                  ? ' (Trunkenbold)'
                  : ''}
                {r.lover
                  ? ' · verliebt'
                  : ''}
              </li>
            ))}
          </ul>
        </Panel>
      )}
        </>
      )}

      <CameraSettings connection={connection} cameraToken={cameraToken} />

      <PlayerTwitchPanel connection={connection} view={view} />

      <Panel title="Klang">
        <VolumeControl />
        <MusicVolumeControl />

        <p className="tiny muted">
          Diese Einstellung gilt nur für dich.
        </p>
      </Panel>
    </main>
  );
}

function PlayerTwitchPanel({
  connection,
  view,
}: {
  connection: Connection<PlayerState>;
  view: PlayerState;
}) {
  const [value, setValue] = useState(view.twitchChannel ?? '');
  const [note, setNote] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Änderungen durch den Spielleiter erscheinen automatisch auch beim Spieler.
  useEffect(() => {
    setValue(view.twitchChannel ?? '');
  }, [view.twitchChannel]);

  async function save() {
    setSaving(true);

    const res = await connection.emit(
      'player:twitch:set',
      value.trim(),
    );

    setSaving(false);

    if (res.ok) {
      setNote(
        value.trim()
          ? 'Twitch-Name gespeichert.'
          : 'Twitch-Name entfernt.',
      );
    } else {
      setNote(res.message ?? 'Das ging nicht.');
    }
  }

  return (
    <Panel title="Twitch-Chat">
      <p className="tiny muted">
        Trage deinen Twitch-Namen ein, damit Stimmen aus deinem Chat
        bei der Bürgermeisterwahl berücksichtigt werden können.
        Du kannst deinen Namen jederzeit korrigieren.
      </p>

      <div className="row">
        <input
          value={value}
          placeholder="Dein Twitch-Name"
          style={{ flex: 1, minWidth: '10rem' }}
          onChange={(event) => {
            setValue(event.target.value);
            setNote(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              void save();
            }
          }}
        />

        <button
          type="button"
          onClick={() => void save()}
          disabled={saving}
        >
          {saving ? 'Speichert…' : 'Speichern'}
        </button>
      </div>

      {view.twitchChannel && (
        <p className="tiny muted">
          Aktuell verbunden: #{view.twitchChannel}
        </p>
      )}

      {note && <p className="tiny">{note}</p>}
    </Panel>
  );
}

/** Ein mittiger Knopf. Nach dem Bestätigen leuchtet er rot. */
function ConfirmButton({
  locked,
  disabled,
  onClick,
}: {
  locked: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <div className="confirm-row">
      <button
        type="button"
        className={`confirm${locked ? ' done' : ''}`}
        disabled={disabled || locked}
        onClick={onClick}
      >
        Bestätigen
      </button>
    </div>
  );
}

/** Bild zur Rolle. Fehlt die Datei, bleibt die Stelle leer. */
function RolePicture({
  role,
  inline,
}: {
  role: string;
  inline?: boolean;
}) {
  const [broken, setBroken] = useState(false);

  if (broken) {
    return null;
  }

  return (
    <img
      className={`role-picture${inline ? ' inline' : ''}`}
      src={`/roles/B_${role}.png?v=3`}
      alt=""
      aria-hidden="true"
      onError={() => setBroken(true)}
    />
  );
}

function CameraSettings({
  connection,
  cameraToken,
}: {
  connection: Connection<PlayerState>;
  cameraToken: string;
}) {
  const view = connection.view;

  const videoRef =
    useRef<HTMLVideoElement | null>(null);

  const cameraSessionRef = useRef<
    Awaited<ReturnType<typeof openMediaMTXCamera>> | null
  >(null);

  const [running, setRunning] =
    useState(false);

  const [previewVisible, setPreviewVisible] =
    useState(true);

  const [note, setNote] =
    useState<string | null>(null);

  const [cameraDevices, setCameraDevices] =
    useState<CameraDevice[]>([]);

  const [selectedCameraId, setSelectedCameraId] =
    useState('');

  const browserHelp =
    getCameraBrowserHelp();

  const lobbyCode =
    view?.lobbyCode ?? '';

  const playerId =
    view?.me.id ?? '';

  const streamPath =
    lobbyCode && playerId
      ? playerCameraPath(
          lobbyCode,
          playerId,
        )
      : '';

  const streamUrl =
    lobbyCode && playerId
      ? playerCameraUrl(
          lobbyCode,
          playerId,
        )
      : '';

  async function refreshCameraDevices(
    preferredDeviceId?: string,
  ) {
    try {
      const devices =
        await getVideoDevices();

      setCameraDevices(devices);

      const preferred =
        preferredDeviceId ||
        selectedCameraId;

      if (
        preferred &&
        devices.some(
          (device) =>
            device.deviceId === preferred,
        )
      ) {
        setSelectedCameraId(preferred);
      } else if (devices.length > 0) {
        setSelectedCameraId(
          devices[0].deviceId,
        );
      } else {
        setSelectedCameraId('');
      }
    } catch (error) {
      console.error(
        '[Kameraliste]',
        error,
      );
    }
  }

  useEffect(() => {
    void refreshCameraDevices();
  }, []);

  async function startCamera() {
    if (
      !lobbyCode ||
      !playerId ||
      !streamPath
    ) {
      setNote(
        'Spielerdaten sind noch nicht verfügbar.',
      );
      return;
    }

    try {
      setNote(
        'Kamera wird gestartet …',
      );

      cameraSessionRef.current?.stop();

      const session =
        await openMediaMTXCamera({
          streamPath,
          token: cameraToken,
          videoDeviceId:
            selectedCameraId ||
            undefined,
        });

      cameraSessionRef.current =
        session;

      const activeVideoTrack =
        session.stream.getVideoTracks()[0];

      const activeDeviceId =
        activeVideoTrack
          ?.getSettings()
          .deviceId;

      await refreshCameraDevices(
        activeDeviceId,
      );

      if (videoRef.current) {
        videoRef.current.srcObject =
          session.stream;

        await videoRef.current.play();
      }

      const result =
        await connection.emit(
          'camera:set',
          streamUrl,
        );

      if (!result.ok) {
        session.stop();
        cameraSessionRef.current =
          null;

        if (videoRef.current) {
          videoRef.current.srcObject =
            null;
        }

        setRunning(false);

        setNote(
          result.message ??
            'Die Kamera konnte nicht beim Server registriert werden.',
        );

        return;
      }

      setRunning(true);
      setPreviewVisible(true);

      setNote(
        'Kamera ist aktiv und im Overlay sichtbar.',
      );

      void connection.emit(
        'camera:status',
        'LIVE',
      );
    } catch (error) {
      console.error(
        '[MediaMTX Kamera]',
        error,
      );

      cameraSessionRef.current?.stop();
      cameraSessionRef.current =
        null;

      if (videoRef.current) {
        videoRef.current.srcObject =
          null;
      }

      setRunning(false);

      void connection.emit(
        'camera:status',
        'ERROR',
      );

      if (
        error instanceof DOMException &&
        (
          error.name === 'NotAllowedError' ||
          error.name === 'SecurityError'
        )
      ) {
        setNote(
          'Der Kamerazugriff wurde nicht erlaubt. Nutze die Anleitung unten, um die Berechtigung wieder zu aktivieren.',
        );
      } else if (
        error instanceof DOMException &&
        error.name === 'NotFoundError'
      ) {
        setNote(
          'Es wurde keine verfügbare Kamera gefunden.',
        );
      } else if (
        error instanceof DOMException &&
        (
          error.name === 'NotReadableError' ||
          error.name === 'AbortError'
        )
      ) {
        setNote(
          'Die Kamera konnte nicht geöffnet werden. Möglicherweise wird sie bereits von einem anderen Programm verwendet.',
        );
      } else {
        setNote(
          error instanceof Error
            ? error.message
            : 'Die Kamera konnte nicht gestartet werden.',
        );
      }
    }
  }

  function stopCamera() {
    cameraSessionRef.current?.stop();
    cameraSessionRef.current =
      null;

    if (videoRef.current) {
      videoRef.current.srcObject =
        null;
    }

    setRunning(false);

    void connection.emit(
      'camera:set',
      null,
    );

    void connection.emit(
      'camera:status',
      'NONE',
    );

    setNote(
      'Kamera wurde beendet.',
    );
  }

  useEffect(() => {
    return () => {
      cameraSessionRef.current?.stop();
      cameraSessionRef.current =
        null;
    };
  }, []);

  return (
    <Panel
      title="Kamera"
      aside={
        <div className="row">
          <CameraDot
            status={
              running
                ? 'LIVE'
                : 'NONE'
            }
          />
          {running && !previewVisible && (
            <button
              type="button"
              onClick={() => setPreviewVisible(true)}
            >
              Vorschau anzeigen
            </button>
          )}
        </div>
      }
    >
      {(!running || previewVisible) && (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))',
          gap: '20px',
          alignItems: 'start',
        }}
      >
        <div className="stack">
          <div
            style={{
              width: '100%',
              aspectRatio: '16 / 9',
              borderRadius: '12px',
              background: '#05070b',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display:
                  running && previewVisible
                    ? 'block'
                    : 'none',
              }}
            />

            {(!running || !previewVisible) && (
              <div
                className="tiny muted"
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  padding: '20px',
                }}
              >
                {running
                  ? 'Deine Vorschau ist ausgeblendet. Die Kamera bleibt im Overlay aktiv.'
                  : 'Kameravorschau'}
              </div>
            )}
          </div>
        </div>

        <div className="stack">
          {!running && (
            <>
              <div className="field">
                <label htmlFor="player-camera-device">
                  Kamera auswählen
                </label>

                <select
                  id="player-camera-device"
                  value={selectedCameraId}
                  onChange={(event) =>
                    setSelectedCameraId(
                      event.target.value,
                    )
                  }
                >
                  {cameraDevices.length === 0 ? (
                    <option value="">
                      Kamera wird beim Start erkannt
                    </option>
                  ) : (
                    cameraDevices.map(
                      (device) => (
                        <option
                          key={device.deviceId}
                          value={device.deviceId}
                        >
                          {device.label}
                        </option>
                      ),
                    )
                  )}
                </select>
              </div>

              <div
                style={{
                  padding: '12px 14px',
                  borderRadius: '12px',
                  background:
                    'rgba(255,255,255,0.05)',
                }}
              >
                <p
                  className="tiny"
                  style={{
                    marginTop: 0,
                    fontWeight: 700,
                  }}
                >
                  📷 Kamera-Hilfe für {browserHelp.browserName}
                </p>

                <p className="tiny muted">
                  {browserHelp.allowText}
                </p>

                <p
                  className="tiny muted"
                  style={{ marginBottom: 0 }}
                >
                  {browserHelp.rejectedText}
                </p>
              </div>
            </>
          )}

          <div className="row">
            {!running ? (
              <button
                type="button"
                className="primary"
                onClick={() =>
                  void startCamera()
                }
              >
                Kamera starten
              </button>
            ) : (
              <button
                type="button"
                className="ghost"
                onClick={stopCamera}
              >
                Kamera stoppen
              </button>
            )}

            {running && (
              <button
                type="button"
                onClick={() =>
                  setPreviewVisible(
                    (value) => !value,
                  )
                }
              >
                {previewVisible
                  ? 'Vorschau ausblenden'
                  : 'Vorschau anzeigen'}
              </button>
            )}
          </div>

          {note && (
            <p className="tiny muted">
              {note}
            </p>
          )}

          <p className="tiny muted">
            Nur dein Kamerabild wird übertragen.
            Das Mikrofon wird nicht verwendet.
          </p>
        </div>
      </div>
      )}
    </Panel>
  );
}

